import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { deleteTrainer } from "../../../../../services/TrainerService.js";
import { getTrainerDetail, getTrainersAvailability, getSchedule } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import { useWaypoint } from "../../../../components/common/waypoint/WaypointProvider.jsx";
import { todayDateStr } from "../../classroomSchedule.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import useScrollLoadMore from "../../../../components/common/useScrollLoadMore";
import TrainerFormModal from "./TrainerFormModal.jsx";
import TrainerRecordModal from "./TrainerRecordModal.jsx";
import TrainersTimelineView from "../DashboardTab/TrainersTimelineView.jsx";
import TrainerDetailModal from "../DashboardTab/TrainerDetailModal.jsx";
/* The page kit, in place of the classroom app's own cls-* vocabulary --
   see LabsTab for the full note. */
import "../../../../components/Trainer/TrainerDashboard.css";
import "../../../../components/common/lms-pages.css";
import { confirm as confirmDialog } from "../../../../components/common/Confirm/confirm";

const PAGE_SIZE = 25;
const PAGE_SIZE_CHOICES = [25, 50, 100];
const DASH = "—";

/* Two views, so they are tabs -- .lp-tabs, the same strip the record
   dialogs use. cls-views was a segmented control of icon buttons that
   turned solid blue when on, a shape nothing else in the project has. */
const VIEWS = [
  { key: "list", label: "List" },
  { key: "timeline", label: "Trainer timeline" },
];

const trainerName = (t) => t?.trainerName || t?.name || t?.trainerEmail || "Unassigned";

/* Sent to the API as `availability`; the counts beside each come back
   with the list, worked out there. */
const AVAILABILITY_OPTIONS = [
  { key: "all", label: "All trainers" },
  { key: "free", label: "Free" },
  { key: "busy", label: "Busy" },
];

const formatDay = (value) =>
  new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/* Today's state, as the API decided it: Busy when a batch of theirs is
   running today, Free otherwise. The title says why. */
const AvailabilityPill = ({ row }) => {
  if (row.availability !== "busy" && row.availability !== "free") return null;
  const busy = row.availability === "busy";
  const count = Number(row.runningBatchesCount) || 0;
  const title = busy
    ? `${count} running ${count === 1 ? "batch" : "batches"}${row.busyUntil ? ` · until ${formatDay(row.busyUntil)}` : ""}`
    : "No batch running today";
  return (
    <span className={`pg-pill ${busy ? "pg-pill--live" : "pg-pill--good"}`} title={title}>
      {busy ? "Busy" : "Free"}
    </span>
  );
};

const TrainersTab = () => {
  const { hasPermission } = useAuth();
  // The bridge: a batch row inside the trainer popup opens that batch.
  const { open: openEntity } = useWaypoint();
  const canAdd = hasPermission("list-trainer", ["add"], "lms");
  const canEdit = hasPermission("list-trainer", ["update"], "lms");
  const canDelete = hasPermission("list-trainer", ["delete"], "lms");

  const [view, setView] = useState("list");

  const [trainers, setTrainers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // A scroll batch, as opposed to a fresh load: the rows on screen stay
  // and the new ones land under them, so the table must not veil itself.
  const [loadingMore, setLoadingMore] = useState(false);
  // Page 1 replaces the list; every page after it appends. A ref because
  // load() reads it after an await, when a re-render may have moved on.
  const appendRef = useRef(false);
  // The sentinel under the list -- crossing it asks for the next batch.
  const listEndRef = useRef(null);

  const [availability, setAvailability] = useState("all");
  const [summary, setSummary] = useState(null);
  // The trainer shown in the view popup (the API's trainer-detail), or null.
  const [detail, setDetail] = useState(null);
  const openingRef = useRef(false);
  const [formModal, setFormModal] = useState(null);

  // Timeline mode — a single date's worth of trainer sessions, backed by the
  // same `getSchedule` endpoint the Dashboard's Trainer timeline view uses
  // (list mode's `getTrainersOverview` has no date scoping, so it can't feed
  // a timeline — a timeline is meaningless without picking a day).
  const [timelineDate, setTimelineDate] = useState(todayDateStr());
  const [timelineTrainerRows, setTimelineTrainerRows] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [selectedTrainerRow, setSelectedTrainerRow] = useState(null);

  const load = useCallback(async () => {
    // Read before anything awaits: a ref reset mid-flight would turn an
    // append into a replace and snap the list back to its first page.
    const isAppend = appendRef.current;
    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);
      const result = await getTrainersAvailability({
        page,
        limit: pageSize,
        search,
        availability,
      });
      const rows = result.data || [];
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setTrainers((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...rows.filter((r) => !seen.has(r.id))];
        });
      } else {
        setTrainers(rows);
      }
      appendRef.current = false;
      setPagination(result.pagination);
      setSummary(result.summary || null);
    } catch (error) {
      console.error("Error loading trainers:", error);
      toast.error("Failed to load trainers");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      appendRef.current = false;
    }
  }, [page, pageSize, search, availability]);

  useEffect(() => {
    load();
  }, [load]);

  const totalTrainers = pagination?.total ?? trainers.length;

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen. No
  // in-flight ref is needed: the page is derived from the rows on screen,
  // so a second fire before the first lands computes the same number and
  // React bails on the identical setState.
  const loadMore = useCallback(() => {
    if (loading || loadingMore) return;
    if (trainers.length >= totalTrainers) return;
    appendRef.current = true;
    // 30 loaded at a size of 15 is page 3, not page 2 -- so the batch size
    // can change mid-list without asking for a page already read. Any
    // overlap a ragged division causes is dropped by the id guard above.
    const nextPage = Math.floor(trainers.length / pageSize) + 1;
    setPage(nextPage);
  }, [loading, loadingMore, trainers.length, totalTrainers, pageSize]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore]);

  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearch(value.trim());
    setPage(1);
  });

  const loadTimeline = useCallback(async () => {
    try {
      setTimelineLoading(true);
      const schedule = await getSchedule({ fromDate: timelineDate, toDate: timelineDate });
      setTimelineTrainerRows(schedule?.trainers || []);
    } catch (error) {
      console.error("Error loading trainer timeline:", error);
      toast.error("Failed to load trainer timeline");
      setTimelineTrainerRows([]);
    } finally {
      setTimelineLoading(false);
    }
  }, [timelineDate]);

  useEffect(() => {
    if (view === "timeline") loadTimeline();
  }, [view, loadTimeline]);

  const handleDelete = async (trainer) => {
    const confirm = await confirmDialog({
      title: "Delete Confirmation",
      text: `Are you sure you want to delete ${trainerName(trainer)}?`,
      confirmButtonText: "Yes, Delete it!",
      tone: 'danger',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteTrainer(trainer.id);
      // The record popup shows the done panel and closes itself after it.
      toast.success("Trainer deleted");
      load();
    } catch (error) {
      toast.error("Failed to delete trainer");
    }
  };

  /* Fetch first, then open -- the popup draws a whole record, the way the
     attendance popup does, rather than a frame waiting on one. A second
     click while the first is still loading is ignored. */
  const openTrainer = async (row) => {
    if (!row?.id || openingRef.current) return;
    openingRef.current = true;
    try {
      const result = await getTrainerDetail(row.id);
      if (result?.trainer) setDetail(result);
      else toast.error("Could not load this trainer");
    } catch (error) {
      toast.error("Could not load this trainer");
    } finally {
      openingRef.current = false;
    }
  };

  /* The trainer is the identity, so the name carries the email under it
     rather than spending a column on it. */
  const headers = useMemo(
    () => [
      {
        title: "Trainer",
        prop: "trainerName",
        isSortable: true,
        cell: (row) => (
          <div className="lp-cell">
            <span className="cls-trainer-name-row">
              <span className="lp-name">{trainerName(row)}</span>
              <AvailabilityPill row={row} />
            </span>
            {row.trainerEmail && <span className="lp-sub">{row.trainerEmail}</span>}
          </div>
        ),
      },
      {
        title: "Specialization",
        prop: "specialization",
        isSortable: true,
        cell: (row) =>
          row.specialization ? (
            <span className="lp-plain">{row.specialization}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Experience",
        prop: "experience",
        isSortable: true,
        cell: (row) =>
          row.experience ? (
            <span className="lp-plain">{row.experience}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Batches",
        prop: "batchesCount",
        isSortable: true,
        cell: (row) => <span>{row.batchesCount ?? 0}</span>,
      },
    ],
    [],
  );

  const scheduleLabel =
    timelineDate === todayDateStr() ? "Today's schedule" : `Schedule for ${timelineDate}`;

  return (
    <div className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h1 className="pg-title">Trainers</h1>
          <p className="pg-sub">
            Who teaches what, and where they are on any given day.
          </p>
        </div>

        {canAdd && view === "list" && (
          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ trainer: null })}
            >
              New trainer
            </button>
          </div>
        )}
      </header>

      <div className="lp-tabs" role="tablist" aria-label="Trainer views">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            id={`cls-trainer-tab-${v.key}`}
            aria-selected={view === v.key}
            aria-controls={`cls-trainer-panel-${v.key}`}
            className={`lp-tab${view === v.key ? " is-on" : ""}`}
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "list" && (
        <div
          role="tabpanel"
          id="cls-trainer-panel-list"
          aria-labelledby="cls-trainer-tab-list"
        >
          <section className="pg-filters" aria-label="Filter trainers">
            <div className="pg-filters-search">
              <div className="pg-field pg-field--search">
                <label className="pg-label" htmlFor="trainer-search">
                  Search
                </label>
                <input
                  id="trainer-search"
                  type="search"
                  className="pg-input pg-input--search"
                  placeholder="Search by name, email or specialization..."
                  value={uiSearchTerm}
                  onChange={(e) => {
                    setUiSearchTerm(e.target.value);
                    runSearch(e.target.value);
                  }}
                />
              </div>

              <div className="pg-field pg-field--tight">
                <label className="pg-label" htmlFor="trainer-availability">
                  Today
                </label>
                <select
                  id="trainer-availability"
                  className="pg-select"
                  value={availability}
                  onChange={(e) => {
                    appendRef.current = false;
                    setAvailability(e.target.value);
                    setPage(1);
                  }}
                >
                  {AVAILABILITY_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                      {summary && Number.isFinite(summary[o.key]) ? ` (${summary[o.key]})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pg-field pg-field--tight">
                <label className="pg-label" htmlFor="trainer-rows">
                  Rows per load
                </label>
                <select
                  id="trainer-rows"
                  className="pg-select"
                  value={pageSize}
                  onChange={(e) => {
                    appendRef.current = false;
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {!loading && trainers.length === 0 ? (
            <div className="pg-state">
              <h3 className="pg-state-title">
                {search || availability !== "all" ? "Nothing matched" : "No trainers yet"}
              </h3>
              <p className="pg-state-text">
                {search
                  ? `No ${availability === "all" ? "" : `${availability} `}trainer matches “${search}”.`
                  : availability !== "all"
                    ? `No trainer is ${availability} today.`
                    : "Trainers added here can be put against a booking."}
              </p>
            </div>
          ) : (
            <>
              <div className="td-list td-list--trainers">
                <NewCustomDataTable
                  data={trainers}
                  headers={headers}
                  totalRecords={totalTrainers}
                  currentPage={page}
                  recordsPerPage={pageSize}
                  serverSide
                  loading={loading}
                  loadingMore={loadingMore}
                  showToolbar={false}
                  showPagination={false}
                  showFooter={false}
                  onRowClick={(row) => openTrainer(row)}
                  validRowClickTagNames="*"
                  rowProps={(row) => ({
                    tabIndex: 0,
                    role: "button",
                    "aria-label": `Open ${trainerName(row)}`,
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openTrainer(row);
                      }
                    },
                  })}
                />
              </div>

              {/* Where the reader is, and the sentinel the observer
                  watches -- crossing it fetches the next batch. */}
              <div className="pg-scroll-foot" ref={listEndRef}>
                {trainers.length < totalTrainers ? (
                  <>
                    <span className="pg-scroll-count">
                      {trainers.length} of {totalTrainers}
                    </span>
                    <span
                      className={`pg-spinner${loadingMore ? "" : " pg-spinner--slot"}`}
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  totalTrainers > 0 && (
                    <span className="pg-scroll-count">
                      All {totalTrainers} trainer{totalTrainers === 1 ? "" : "s"} loaded
                    </span>
                  )
                )}
              </div>
            </>
          )}
        </div>
      )}

      {view === "timeline" && (
        <div
          role="tabpanel"
          id="cls-trainer-panel-timeline"
          aria-labelledby="cls-trainer-tab-timeline"
        >
          <section className="pg-filters" aria-label="Timeline date">
            <div className="pg-filters-row">
              <div className="pg-field pg-field--tight">
                <label className="pg-label" htmlFor="trainer-tl-date">
                  For date
                </label>
                <input
                  id="trainer-tl-date"
                  type="date"
                  className="pg-input"
                  value={timelineDate}
                  onChange={(e) => setTimelineDate(e.target.value)}
                />
              </div>

              {timelineDate !== todayDateStr() && (
                <div className="pg-filters-actions">
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setTimelineDate(todayDateStr())}
                  >
                    Jump to today
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* What the colours on the timeline mean, said once. */}
          <div className="cls-legend">
            <span className="pg-pill pg-pill--good">Free</span>
            <span className="pg-pill pg-pill--live">Occupied</span>
            <span className="pg-pill pg-pill--warn">Shifted</span>
            <span className="pg-pill pg-pill--bad">Absent</span>
          </div>

          {timelineLoading ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading the day&rsquo;s schedule...</p>
            </div>
          ) : (
            <TrainersTimelineView
              trainerRows={timelineTrainerRows}
              onTrainerClick={setSelectedTrainerRow}
            />
          )}
        </div>
      )}

      {/* The trainer in full. It closes itself before every hand-off --
          the form, the delete confirm, or a batch opened through
          Waypoint -- so two modals are never stacked. */}
      {detail && (
        <TrainerRecordModal
          detail={detail}
          canEdit={canEdit}
          canDelete={canDelete}
          onClose={() => setDetail(null)}
          onEdit={(trainer) => {
            setFormModal({ trainer });
          }}
          onDelete={(trainer) => {
            handleDelete(trainer);
          }}
          onOpenBatch={(batchId) => {
            setDetail(null);
            openEntity("batch", batchId);
          }}
        />
      )}

      {formModal && (
        <TrainerFormModal
          trainer={formModal.trainer}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            // Saved: the form shows its done panel and closes itself; the
            // view popup behind it, which would show the old record, closes
            // now (the form is its sibling, not its child).
            setDetail(null);
            load();
          }}
        />
      )}

      {selectedTrainerRow && (
        <TrainerDetailModal
          row={selectedTrainerRow}
          canEdit={canEdit}
          todayStr={todayDateStr()}
          scheduleLabel={scheduleLabel}
          rangeNote={null}
          onClose={() => setSelectedTrainerRow(null)}
          onChanged={loadTimeline}
        />
      )}
    </div>
  );
};

export default TrainersTab;
